/**
 * backPointer — pure resolver for the `users/{uid}/account_membership/main`
 * back-pointer doc (group-threading add-people hardening).
 *
 * The primary `resolveAccount()` (client + server) keys on a top-level
 * `accountId`, but `rbacAcceptInvitation` historically wrote a multi-account
 * shape (`{ defaultAccountId, accountIds[], memberships[] }`) with NO
 * `accountId` — so a freshly-invited user resolved to null ("Account not
 * migrated") despite a valid member doc, and had to be repaired by hand in
 * Firestore. This accepts BOTH shapes (and self-heals old docs) by falling back
 * in order: `accountId` → `defaultAccountId` → first ACTIVE membership → first
 * membership.
 *
 * Dependency-free (no firebase) so it unit-tests in isolation. Mirrors
 * `pickBackPointerAccountId` in functions/src/prest0nSession/accountResolver.ts
 * (the src/ and functions/ trees can't share a module).
 */

const asTrimmed = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function pickBackPointerAccountId(
  data: Record<string, unknown> | undefined | null,
): string | null {
  if (!data) return null;
  const direct = asTrimmed(data.accountId);
  if (direct) return direct;
  const def = asTrimmed(data.defaultAccountId);
  if (def) return def;
  const memberships = Array.isArray(data.memberships)
    ? (data.memberships as Array<Record<string, unknown>>)
    : [];
  const active = memberships.find(
    (m) => asTrimmed(m?.accountId) && String(m?.status ?? 'active').toLowerCase() === 'active',
  );
  if (active) return asTrimmed(active.accountId);
  const first = memberships.find((m) => asTrimmed(m?.accountId));
  return first ? asTrimmed(first.accountId) : null;
}
